import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import AmountInput from 'components/common/AmountInput';
import {
  CYRILLIC_REGEXP,
  validateCommentFieldByPaymentMethod
} from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
import ValidatePaymentDetailsModel from 'components/NewPaymentFormV2/models/ValidatePaymentDetailsModel';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import Input from 'uikit/Input/Input';
import Button from 'uikit/Button/Button';
import './PaymentDetailsStep.scss';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';
import { PAYMENT_METHOD } from '../../../../../common/constants';
import { trimTextContent } from '../../../../../../services/utils';
import { getMaxDescriptionSize } from '../../../../../common/PaymentForm/utils';

const PaymentDetailsStep = ({ newPaymentStore, customerNumber }) => {
  const i18n = useContext(i18nContext);
  const isCryptoPayment = !!newPaymentStore.paymentDetailsStep?.isCryptoPayment;

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      amount: newPaymentStore.paymentDetailsStep?.amount || '',
      transferDetails: newPaymentStore.paymentDetailsStep?.transferDetails || ''
    },
    validationSchema: Yup.object().shape({
      amount: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      transferDetails: Yup.string().when([], {
        is: () => !isCryptoPayment,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .test(
              'openpayd-comment-validation',
              newPaymentStore.newPaymentFormData?.paymentMethod === PAYMENT_METHOD.SWIFT
                ? i18n.getMessage('transfer.form.error.comment.SWIFT')
                : i18n.getMessage('transfer.form.error.comment.OTHER_METHODS'),
              (comment) =>
                validateCommentFieldByPaymentMethod(
                  newPaymentStore.newPaymentFormData?.paymentMethod,
                  trimTextContent(comment)
                )
            )
            .max(
              getMaxDescriptionSize(newPaymentStore.newPaymentFormData?.paymentMethod),
              i18n.getMessage('client.validation.message.maxSize', {
                size: getMaxDescriptionSize(newPaymentStore.newPaymentFormData?.paymentMethod)
              })
            ),
        otherwise: (schema) => schema
      })
    }),
    onSubmit: async (values) => {
      const requestData = ValidatePaymentDetailsModel({ ...newPaymentStore.paymentDetailsStep, ...values });

      newPaymentStore.updatePaymentDetailsStep(values);

      await newPaymentStore.validatePaymentDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  return (
    <form onSubmit={form.handleSubmit} className={'new-payment-payment-details-step'}>
      <div className={'new-payment-payment-details-step-inputs-wrapper'}>
        <AmountInput
          className={'new-payment-payment-details-step-input'}
          name={'amount'}
          label={i18n.getMessage('templates.form.amount.label')}
          placeholder={i18n.getMessage('templates.form.amount.placeholder')}
          value={`${values.amount}`}
          error={errors.amount || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'amount')}
          onChange={handleChange}
          precision={newPaymentStore.newPaymentFormData?.isCryptoPayment ? CRYPTO_CURRENCY_PRECISION : 2}
          isRequired
        />
        {!isCryptoPayment && (
          <Input
            className={'new-payment-payment-details-step-input'}
            name={'transferDetails'}
            label={i18n.getMessage('templates.form.transferDetails.label')}
            placeholder={i18n.getMessage('templates.form.transferDetails.placeholder')}
            value={values.transferDetails}
            error={
              errors.transferDetails ||
              getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'transferDetails')
            }
            onChange={handleChange}
            isRequired
          />
        )}
      </div>
      <div className={'new-payment-step-buttons'}>
        <Button
          className={'new-payment-payment-details-step-next-button'}
          type={'primary'}
          roleType={'submit'}
          onClick={handleSubmit}
          isDisabled={newPaymentStore.isLoading}
        >
          {i18n.getMessage('templates.form.button.next')}
        </Button>
      </div>
    </form>
  );
};

PaymentDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore
}))(observer(PaymentDetailsStep));
